import React, { useEffect, useState } from 'react';
import scheduledPostsAPI from '../../services/scheduledPostsAPI';
import './ScheduledPostsManagement.css';

/**
 * Scheduled Posts Management Component
 * Manages both manual and AI-powered scheduled blog posts
 */
const ScheduledPostsManagement = ({ onRefresh }) => {
  const [scheduledPosts, setScheduledPosts] = useState([]);
  const [aiSchedules, setAISchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showHistory, setShowHistory] = useState(null);
  const [executionHistory, setExecutionHistory] = useState([]);

  useEffect(() => {
    loadScheduledPosts();
  }, [onRefresh]);

  const loadScheduledPosts = async () => {
    try {
      setLoading(true);
      const [postsResponse, schedulesResponse] = await Promise.all([
        scheduledPostsAPI.getScheduledPosts(),
        scheduledPostsAPI.getAISchedules()
      ]);

      if (postsResponse.success) {
        setScheduledPosts(postsResponse.posts || []);
      } else {
        setError(postsResponse.message);
      }

      if (schedulesResponse.success) {
        setAISchedules(schedulesResponse.schedules || []);
      }
    } catch (err) {
      console.error('Error loading scheduled posts:', err);
      setError(err.message || 'Failed to load scheduled posts');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (id) => {
    try {
      const response = await scheduledPostsAPI.toggleScheduledPost(id);

      if (response.success) {
        // Update local state
        setScheduledPosts(posts =>
          posts.map(p => p._id === id ? { ...p, isActive: response.isActive } : p)
        );
      } else {
        alert(response.message || 'Failed to toggle scheduled post');
      }
    } catch (err) {
      console.error('Error toggling scheduled post:', err);
      alert(err.message || 'Failed to toggle scheduled post');
    }
  };

  const handleExecuteNow = async (id) => {
    if (!window.confirm('Execute this scheduled post now? This will generate and publish the content immediately.')) {
      return;
    }

    try {
      const response = await scheduledPostsAPI.executeScheduledPost(id);

      if (response.success) {
        alert('Scheduled post executed successfully!');
        loadScheduledPosts();
      } else {
        alert(response.message || 'Failed to execute scheduled post');
      }
    } catch (err) {
      console.error('Error executing scheduled post:', err);
      alert(err.message || 'Failed to execute scheduled post');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this scheduled post? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await scheduledPostsAPI.deleteScheduledPost(id);

      if (response.success) {
        setScheduledPosts(posts => posts.filter(p => p._id !== id));
      } else {
        alert(response.message || 'Failed to delete scheduled post');
      }
    } catch (err) {
      console.error('Error deleting scheduled post:', err);
      alert(err.message || 'Failed to delete scheduled post');
    }
  };

  const handleViewHistory = async (id) => {
    try {
      const response = await scheduledPostsAPI.getExecutionHistory(id);

      if (response.success) {
        setExecutionHistory(response.history || []);
        setShowHistory(id);
      } else {
        alert(response.message || 'Failed to load execution history');
      }
    } catch (err) {
      console.error('Error loading execution history:', err);
      alert(err.message || 'Failed to load execution history');
    }
  };

  const handleTriggerAllSchedulers = async () => {
    if (!window.confirm('Manually trigger all schedulers? This will process all due scheduled posts immediately.')) {
      return;
    }

    try {
      const response = await scheduledPostsAPI.triggerAllSchedulers();

      if (response.success) {
        alert('All schedulers triggered successfully!');
        loadScheduledPosts();
      } else {
        alert(response.message || 'Failed to trigger schedulers');
      }
    } catch (err) {
      console.error('Error triggering schedulers:', err);
      alert(err.message || 'Failed to trigger schedulers');
    }
  };

  const handleToggleAISchedule = async (id) => {
    try {
      const response = await scheduledPostsAPI.toggleAISchedule(id);

      if (response.success) {
        setAISchedules(schedules =>
          schedules.map(s => s._id === id ? { ...s, isActive: response.schedule.isActive } : s)
        );
      } else {
        alert(response.message || 'Failed to toggle AI schedule');
      }
    } catch (err) {
      console.error('Error toggling AI schedule:', err);
      alert(err.message || 'Failed to toggle AI schedule');
    }
  };

  const handleDeleteAISchedule = async (id) => {
    if (!window.confirm('Delete this AI schedule? This cannot be undone.')) {
      return;
    }

    try {
      const response = await scheduledPostsAPI.deleteAISchedule(id);

      if (response.success) {
        setAISchedules(schedules => schedules.filter(s => s._id !== id));
      } else {
        alert(response.message || 'Failed to delete AI schedule');
      }
    } catch (err) {
      console.error('Error deleting AI schedule:', err);
      alert(err.message || 'Failed to delete AI schedule');
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString();
  };

  const getStatusBadge = (post) => {
    if (!post.isActive) {
      return <span className="badge badge-inactive">Inactive</span>;
    }

    switch (post.status) {
      case 'pending':
        return <span className="badge badge-pending">Pending</span>;
      case 'published':
        return <span className="badge badge-published">Published</span>;
      case 'failed':
        return <span className="badge badge-failed">Failed</span>;
      case 'cancelled':
        return <span className="badge badge-cancelled">Cancelled</span>;
      default:
        return <span className="badge badge-unknown">{post.status}</span>;
    }
  };

  const getSchedulingInfo = (post) => {
    if (post.scheduling.type === 'once') {
      return (
        <div className="scheduling-info">
          <div className="scheduling-type">One-time</div>
          <div className="scheduling-date">
            {formatDate(post.scheduling.scheduledFor)}
          </div>
        </div>
      );
    } else {
      return (
        <div className="scheduling-info">
          <div className="scheduling-type">
            Recurring ({post.scheduling.recurring?.frequency})
          </div>
          <div className="scheduling-next">
            Next: {formatDate(post.scheduling.nextExecution)}
          </div>
        </div>
      );
    }
  };

  if (loading) {
    return (
      <div className="scheduled-posts-management">
        <div className="loading">Loading scheduled posts...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="scheduled-posts-management">
        <div className="error-message">
          <p>Error: {error}</p>
          <button onClick={loadScheduledPosts}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="scheduled-posts-management">
      <div className="header">
        <h2>Scheduled Posts</h2>
        <div className="header-actions">

          <button className="btn-secondary" onClick={handleTriggerAllSchedulers}>
            Trigger All Schedulers
          </button>
        </div>
      </div>

      {scheduledPosts.length === 0 && aiSchedules.length === 0 ? (
        <div className="empty-state">
          <p>No scheduled posts found.</p>
          <p className="hint">Create a scheduled post from the Blog Post Editor.</p>
        </div>
      ) : (
        <>
          {/* AI Schedules Section */}
          {aiSchedules.length > 0 && (
            <div className="ai-schedules-section">
              <h2 className="section-title">AI Recurring Schedules</h2>
              <div className="scheduled-posts-list">
                {aiSchedules.map(schedule => (
                  <div key={schedule._id} className={`scheduled-post-card ai-schedule ${!schedule.isActive ? 'inactive' : ''}`}>
                    <div className="post-header">
                      <h3>{schedule.description}</h3>
                      <span className={`status-badge ${schedule.isActive ? 'active' : 'inactive'}`}>
                        {schedule.isActive ? 'Active' : 'Paused'}
                      </span>
                    </div>

                    <div className="post-body">
                      <div className="schedule-info">
                        <div className="schedule-detail">
                          <strong>Schedule:</strong>{' '}
                          {schedule.scheduleType === 'weekly' && `Every ${['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][schedule.dayOfWeek]} at ${schedule.time}`}
                          {schedule.scheduleType === 'biweekly' && `Every 2 weeks on ${['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][schedule.dayOfWeek]} at ${schedule.time}`}
                          {schedule.scheduleType === 'monthly' && `Monthly on day ${schedule.dayOfMonth} at ${schedule.time}`}
                        </div>
                        <div className="schedule-detail">
                          <strong>Tone:</strong> {schedule.tone}
                          {' | '}
                          <strong>Length:</strong> {schedule.length}
                        </div>
                        <div className="schedule-detail">
                          <strong>Progress:</strong> {schedule.currentMonthPosts || 0} / {schedule.postsPerMonth} posts this month
                        </div>
                        {schedule.lastExecutionStatus && (
                          <div className="schedule-detail">
                            <strong>Last Status:</strong>{' '}
                            <span className={`status-indicator status-${schedule.lastExecutionStatus}`}>
                              {schedule.lastExecutionStatus === 'success' && '✅ Success'}
                              {schedule.lastExecutionStatus === 'quota_exceeded' && '⚠️ Quota Exceeded'}
                              {schedule.lastExecutionStatus === 'error' && '❌ Error'}
                              {schedule.lastExecutionStatus === 'failed' && '❌ Failed'}
                            </span>
                          </div>
                        )}
                        {schedule.lastExecutionError && (
                          <div className="schedule-detail error-detail">
                            <strong>Error:</strong> {schedule.lastExecutionError}
                          </div>
                        )}
                        {schedule.nextScheduledRun && (
                          <div className="schedule-detail">
                            <strong>Next Run:</strong> {formatDate(schedule.nextScheduledRun)}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="post-actions">
                      <button
                        className={`btn-toggle ${schedule.isActive ? 'active' : 'inactive'}`}
                        onClick={() => handleToggleAISchedule(schedule._id)}
                      >
                        {schedule.isActive ? '⏸️ Pause' : '▶️ Activate'}
                      </button>

                      <button
                        className="btn-delete"
                        onClick={() => handleDeleteAISchedule(schedule._id)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Manual Scheduled Posts Section */}
          {scheduledPosts.length > 0 && (
            <div className="manual-posts-section">
              <h2 className="section-title">Manual Scheduled Posts</h2>
              <div className="scheduled-posts-list">
                {scheduledPosts.map(post => (
                  <div key={post._id} className={`scheduled-post-card ${!post.isActive ? 'inactive' : ''}`}>
                    <div className="post-header">
                      <h3>{post.title}</h3>
                      {getStatusBadge(post)}
                    </div>

                    <div className="post-body">
                      {getSchedulingInfo(post)}

                      <div className="post-meta">
                        <div className="meta-item">
                          <strong>Auto Publish:</strong> {post.autoPublish ? 'Yes' : 'No (save as draft)'}
                        </div>
                        <div className="meta-item">
                          <strong>Attempts:</strong> {post.attemptCount} / {post.maxAttempts}
                        </div>
                        {post.executionHistory && post.executionHistory.length > 0 && (
                          <div className="meta-item">
                            <strong>Executions:</strong> {post.executionHistory.length}
                          </div>
                        )}
                      </div>

                      {post.excerpt && (
                        <div className="post-excerpt">
                          <p>{post.excerpt.substring(0, 150)}...</p>
                        </div>
                      )}
                    </div>

                    <div className="post-actions">
                      <button
                        className={`btn-toggle ${post.isActive ? 'active' : 'inactive'}`}
                        onClick={() => handleToggleActive(post._id)}
                      >
                        {post.isActive ? '⏸️ Pause' : '▶️ Activate'}
                      </button>

                      <button
                        className="btn-execute"
                        onClick={() => handleExecuteNow(post._id)}
                        disabled={!post.isActive}
                      >
                        Execute Now
                      </button>

                      <button
                        className="btn-history"
                        onClick={() => handleViewHistory(post._id)}
                      >
                        History
                      </button>

                      <button
                        className="btn-delete"
                        onClick={() => handleDelete(post._id)}
                      >
                        Delete
                      </button>
                    </div>

                    {/* Execution History Modal */}
                    {showHistory === post._id && (
                      <div className="execution-history-modal">
                        <div className="modal-overlay" onClick={() => setShowHistory(null)} />
                        <div className="modal-content">
                          <div className="modal-header">
                            <h3>Execution History</h3>
                            <button className="close-btn" onClick={() => setShowHistory(null)}>×</button>
                          </div>
                          <div className="modal-body">
                            {executionHistory.length === 0 ? (
                              <p>No execution history yet.</p>
                            ) : (
                              <table className="history-table">
                                <thead>
                                  <tr>
                                    <th>Date</th>
                                    <th>Status</th>
                                    <th>Post</th>
                                    <th>Error</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {executionHistory.map((exec, index) => (
                                    <tr key={index}>
                                      <td>{formatDate(exec.executedAt)}</td>
                                      <td>
                                        <span className={`badge badge-${exec.success ? 'success' : 'failed'}`}>
                                          {exec.success ? 'Success' : 'Failed'}
                                        </span>
                                      </td>
                                      <td>
                                        {exec.postId ? (
                                          <a href={`/admin/blog/edit/${exec.postId._id}`} target="_blank" rel="noopener noreferrer">
                                            {exec.postId.title}
                                          </a>
                                        ) : (
                                          'N/A'
                                        )}
                                      </td>
                                      <td>{exec.error || '-'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ScheduledPostsManagement;

